import { t, Trans } from '@lingui/macro'
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  Icon,
  IconButton,
  LinearProgress, Paper,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Radio from '@mui/material/Radio'
import { dateFormat } from 'app/appSettings'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import {
  deleteDocumentByFlow,
  getDocumentsByEntity,
  parseDocument,
  uploadFileToMultipleObjects
} from 'app/services/sfAuth/sfData/sfFiles'
import { ErrorMessage, Field, useField } from 'formik'
import { RadioGroup } from 'formik-material-ui'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React from 'react'
import Dropzone from 'react-dropzone'
import NumberFormat from 'react-number-format'
import ScrollBar from 'react-perfect-scrollbar'
import { myI18n } from 'translation/I18nConnectedProvider'
import CustomDatePickerWithError from '../common-components/date-pickers/CustomDatePickerWithError'
import { ErrorLabel } from '../common-components/forms/ErrorLabel'
import { TextFieldWithFormik } from '../common-components/with-formik/TextFieldWithFormik'
import { getLabelFromTranslationData } from '../common/translationsCommon'
import PreviouslyAnsweredField from './PreviouslyAnsweredField'
import {
  fixedQuestionAnswers,
  indexToOption,
  questionTypes
} from './QuestionWidget'
import {
  SURVEY_SLIDER_DEFAULT_MAX,
  SURVEY_SLIDER_DEFAULT_MIN
} from './SurveyCreationSliderFields'
import { SurveyGroupQuestion } from './SurveyGroupQuestion'
import { SurveySelectOneQuestion } from './SurveySelectOneQuestion'
import { nonApplicableSuffix, questionToInitial } from './SurveyTab'
import { SurveyTextField } from './SurveyTextField'

function QuestionInputText ({
  fieldId,
  hideError,
  question,
  disabled,
  disableFields,
  completedSurveys,
  version,
  i18n,
  setFieldValue
}) {
  const numeric = question.type === questionTypes.INPUT_NUMBER.id
  const textArea = question.type === questionTypes.INPUT_TEXTAREA.id
  const {
    maxCharacters,
    max,
    min,
    rows,
    tooltip,
    tooltipAtBottom,
    isGroup,
    groupQuestions = [],
    isRequired,
    allowRowsToExpand
  } = question

  return (
    <div>
      {tooltip && !tooltipAtBottom && renderSurveyQuestionToolitp(tooltip)}
      {!disabled && completedSurveys && (
        <PreviouslyAnsweredField
          setFieldValue={setFieldValue}
          completedSurveys={completedSurveys}
          item={question}
          version={version}
        />
      )}
      {isGroup
        ? <Box sx={{ padding: '16px' }}>
          {groupQuestions.map((item, qIndex) => {
            if (item.id && disableFields.includes(item.id)) {
              return null
            }
            return (
              <Box key={qIndex}>
                <Grid container direction='row' key={qIndex} justifyContent='center' alignItems='center'>
                  <Grid
                    item
                    sx={{
                      width: '170px !important'
                    }}
                  >
                    <Typography className='subquestion-header'>
                      {getLabelFromTranslationData({
                        langVersion: version,
                        data: item.titleValue,
                        i18n
                      })}{isRequired && '\u00A0*'}
                    </Typography>
                  </Grid>
                  <Grid item container xs>
                    <SurveyTextField
                      max={max}
                      min={min}
                      id={`${fieldId}.${qIndex}`}
                      allowRowsToExpand={allowRowsToExpand}
                      rows={rows}
                      multiline={textArea}
                      disabled={disabled}
                      numeric={numeric}
                      hideError={hideError}
                      maxCharacters={maxCharacters}
                      required={isRequired}
                    />
                  </Grid>
                </Grid>
                {qIndex !== groupQuestions.length - 1 && <Divider sx={{ marginTop: '16px', marginBottom: '16px' }} />}
              </Box>

            )
          })}

        </Box>
        : (
          <SurveyTextField
            max={max}
            min={min}
            id={fieldId}
            rows={rows}
            allowRowsToExpand={allowRowsToExpand}
            multiline={textArea}
            disabled={disabled}
            numeric={numeric}
            hideError={hideError}
            maxCharacters={maxCharacters}
            required={isRequired}
          />
          )}
      {tooltip && tooltipAtBottom && renderSurveyQuestionToolitp(tooltip, true)}
    </div>
  )
}

function QuestionUploadFiles ({
  fieldId,
  question,
  disabled,
  questionValue,
  setFieldValue,
  completedSurveys,
  opportunity,
  entityFiles = [],
  surveyId
}) {
  const [fileList, setFileList] = React.useState(
    entityFiles.filter(item => item.tags && item.tags.includes(fieldId))
  )
  const { enqueueSnackbar } = useSnackbar()
  const { network } = useGlobalQueries()

  const isDisabled =
    disabled || !network.id || !opportunity || !opportunity.reportId

  const handleOnDrop = files => {
    console.log('handleOnDrop', files, network)
    const listCount = fileList.length
    const uploadId = listCount + 1
    const file = files[0]
    setFileList(
      [...fileList].concat({
        uploadId,
        name: file.name,
        progress: true
      })
    )
    const reader = new FileReader()
    reader.onabort = () => console.log('file reading was aborted')
    reader.onerror = () => console.log('file reading has failed')
    reader.onload = () => {
      const binaryStr = reader.result
      enqueueSnackbar(<Trans>Uploading File</Trans>, {
        variant: 'info'
      })
      let additionalTags = ''
      let mainObjId
      if (opportunity.reportId) {
        additionalTags = ';;granteeReport'
        mainObjId = opportunity.reportId
      }
      if (!mainObjId) {
        return
      }
      uploadFileToMultipleObjects({
        name: file.name,
        description: question.fileDescription,
        tags: question.id + additionalTags,
        fileData: binaryStr,
        networkId: network.id,
        mainId: mainObjId,
        ids: [opportunity.id]
      })
        .then(
          res => {
            console.log('files', res)
            enqueueSnackbar(<Trans>Uploaded File</Trans>, {
              variant: 'success'
            })
            const otherFiles = []
            res.forEach(doc => {
              otherFiles.push(doc)
            })
            const list = otherFiles.map(item => parseDocument(item))
            console.log('uploaded File', list)
            setFileList(list)
          },
          reject => {
            getDocumentsByEntity(opportunity.reportId).then(res => {
              enqueueSnackbar(
                <Trans>
                  Error ocurred while uploading file to some objects
                </Trans>,
                {
                  variant: 'error'
                }
              )
              console.log('files', res)
              const otherFiles = []
              res.forEach(doc => {
                otherFiles.push(doc)
              })
              const list = otherFiles.map(item => parseDocument(item))
              console.log('uploaded File', list)
              setFileList(list)
            })
          }
        )
        .catch(error => {
          console.warn('cant upload file', error)
          enqueueSnackbar(<Trans>Error Uploaded File</Trans>, {
            variant: 'error'
          })
          setFileList(fileList.filter(item => item.id !== uploadId))
        })
    }
    reader.readAsArrayBuffer(file)
  }

  const onRemoveItem = id => {
    enqueueSnackbar(<Trans>Deleting File</Trans>, {
      variant: 'info'
    })
    deleteDocumentByFlow(id).then(result => {
      enqueueSnackbar(<Trans>Deleted File</Trans>, {
        variant: 'success'
      })
      setFileList(fileList.filter(item => item.id !== id))
    })
  }

  const createTable = ({ disabled }) => {
    const columns = [t`Name`, t`Remove`]
    return (
      (
        <TableContainer component={Paper}>
          <Table style={{ whiteSpace: 'pre' }}>
            <colgroup>
              <col width='85%' />
              <col width='15%' />
            </colgroup>
            <TableHead>
              <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                {columns.map((item, i) => (
                  <TableCell key={i}>
                    <p
                      style={{
                        textShadow: '2px 2px 0px rgba(63,107,169, 0.15)',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        paddingRight: i === 1 && 20
                      }}
                    >
                      {myI18n._(item)}
                    </p>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fileList.map((item, index) => {
                const iconDisabled = disabled || item.progress
                return [
                  <TableRow key={index}>
                    <TableCell>
                      <div style={{ paddingLeft: 35 }}>
                        <a color='primary' href={item.url}>
                          {item.name}
                        </a>
                        {question.showDescription && (
                          <div>{item.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          paddingRight: 20
                        }}
                      >
                        <Tooltip title={myI18n._(t`Remove file`)}>
                          <IconButton
                            variant='filled'
                            disabled={iconDisabled}
                            onClick={() => {
                              onRemoveItem(item.id)
                            }}
                            size='large'
                          >
                            <Icon
                              className={
                              iconDisabled
                                ? ''
                                : 'material-icons MuiIcon-root MuiIcon-colorError'
                            }
                              variant='filled'
                            >
                              delete
                            </Icon>
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>,
                  item.progress && (
                    <TableRow key={fileList.length + 1}>
                      <TableCell colSpan={3}>
                        <LinearProgress />
                      </TableCell>
                    </TableRow>
                  )
                ]
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )
    )
  }

  return (
    <div>
      {question.tooltip &&
        !question.tooltipAtBottom &&
        renderSurveyQuestionToolitp(question.tooltip)}
      <div>{createTable({ disabled })}</div>
      <Dropzone
        disabled={isDisabled}
        multiple={false}
        maxFiles={1}
        onDrop={files => {
          handleOnDrop(files)
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps()}
            className={isDisabled ? 'dropbox_disabled' : 'dropbox'}
          >
            <section>
              <div>
                <input {...getInputProps()} />
                <div
                  style={{
                    textAlign: 'center'
                    // color: !item.income.id && 'red'
                  }}
                >
                  <span>
                    {isDisabled && (
                      <>
                        <Trans>Upload disabled</Trans>
                        <br />
                      </>
                    )}
                    <Trans>
                      Drag 'n' drop file here, or click to select file
                    </Trans>
                  </span>
                </div>
              </div>
              <div />
            </section>
          </div>
        )}
      </Dropzone>
      {question.tooltip &&
        question.tooltipAtBottom &&
        renderSurveyQuestionToolitp(question.tooltip, true)}
    </div>
  )
}

function QuestionNumericSlider ({
  fieldId,
  hideError,
  question,
  disabled,
  questionValue,
  setFieldValue,
  disableFields,
  version,
  completedSurveys,
  i18n
}) {
  const minValue = Number(question.min) || SURVEY_SLIDER_DEFAULT_MIN
  const maxValue = Number(question.max) || SURVEY_SLIDER_DEFAULT_MAX
  let isValid =
    !question.isGroup && questionValue <= maxValue && questionValue >= minValue
  const [tempValue, setTempValue] = React.useState(null)
  const [subMoved, setSubMoved] = React.useState(null)
  return (
    <div>
      {question.tooltip && !question.tooltipAtBottom && (
        <div style={{ marginBottom: 5 }}>{renderSurveyQuestionToolitp(question.tooltip)}</div>
      )}
      {!disabled && completedSurveys && (
        <PreviouslyAnsweredField
          setFieldValue={setFieldValue}
          completedSurveys={completedSurveys}
          item={question}
          version={version}
        />
      )}
      {question.isGroup
        ? (
            question.groupQuestions
              ? (
                  question.groupQuestions.map((item, qIndex) => {
                    if (item.id && disableFields.includes(item.id)) {
                      return null
                    }
                    const qValue = questionValue ? questionValue[qIndex] : minValue
                    isValid = qValue <= maxValue && qValue >= minValue
                    return (
                      <Grid key={qIndex} container direction='row' alignItems='center'>
                        <Grid style={{ flex: 1, margin: 10 }}>
                          <Typography
                            className='survey-question-text'
                            style={{
                              textAlign: 'center',
                              wordBreak: 'break-word'
                            }}
                          >
                            {getLabelFromTranslationData({
                              langVersion: version,
                              data: item.titleValue,
                              i18n
                            })}
                          </Typography>
                        </Grid>
                        <Grid
                          container
                          style={{ flex: 2, marginTop: 20 }}
                          direction='column'
                          justifyContent='flex-start'
                        >
                          <Grid
                            container
                            direction='row'
                            wrap='nowrap'
                            style={{ paddingLeft: 15, paddingRight: 15 }}
                          >
                            <div
                              style={{
                                flexGrow: 1,
                                paddingTop: 20,
                                paddingBottom: 20,
                                paddingLeft: 50,
                                paddingRight: 50
                              }}
                            >
                              <Slider
                                className={!qValue && 'slider-muted'}
                                style={{
                                  color: !qValue
                                    ? 'secondary'
                                    : disabled
                                      ? 'rgba(0, 0, 0, 0.26)'
                                      : 'primary'
                                }}
                                onChangeCommitted={(event, newValue) => {
                                  setFieldValue(`${fieldId}.${qIndex}`, newValue)
                                  setTempValue(null)
                                  setSubMoved(null)
                                }}
                                onChange={(event, newValue) => {
                                  setSubMoved(qIndex)
                                  setTempValue(newValue)
                                }}
                                value={
                          subMoved === qIndex
                            ? tempValue
                            : isValid
                              ? qValue
                              : minValue
                        }
                                step={1}
                                disabled={disabled}
                                min={minValue}
                                max={maxValue}
                                marks={[
                                  { value: minValue, label: minValue },
                                  { value: maxValue, label: maxValue }
                                ]}
                                valueLabelDisplay='auto'
                              />
                            </div>
                            <Grid
                              item
                              container
                              style={{ width: 100 }}
                              justifyContent='center'
                              alignItems='center'
                            >
                              <Typography style={{ fontSize: 18 }}>{qValue}</Typography>
                            </Grid>
                          </Grid>
                          {!hideError && (
                            <ErrorMessage name={`${fieldId}.${qIndex}`}>
                              {msg => (
                                <ErrorLabel>
                                  {msg}
                                </ErrorLabel>
                              )}
                            </ErrorMessage>
                          )}
                          <div style={{ marginBottom: 20 }} />
                        </Grid>
                      </Grid>
                    )
                  })
                )
              : null
          )
        : (
          <div>
            <Grid
              container
              direction='row'
              wrap='nowrap'
              style={{ paddingLeft: 15, paddingRight: 15 }}
            >
              <div
                style={{
                  flexGrow: 1,
                  paddingTop: 40,
                  paddingBottom: 20,
                  paddingLeft: 50,
                  paddingRight: 50
                }}
              >
                <Slider
                  className={!questionValue && 'slider-muted'}
                  style={{
                    color: !questionValue
                      ? 'secondary'
                      : disabled
                        ? 'rgba(0, 0, 0, 0.26)'
                        : 'primary'
                  }}
                  onChangeCommitted={(event, newValue) => {
                    setFieldValue(`${fieldId}`, newValue)
                    setTempValue(null)
                  }}
                  onChange={(event, newValue) => {
                    setTempValue(newValue)
                  }}
                  value={tempValue || (isValid ? questionValue : minValue)}
                  step={1}
                  disabled={disabled}
                  min={minValue}
                  max={maxValue}
                  marks={[
                    { value: minValue, label: minValue },
                    { value: maxValue, label: maxValue }
                  ]}
                  valueLabelDisplay='auto'
                />
              </div>
              <Grid
                item
                container
                style={{ width: 100 }}
                justifyContent='center'
                justifyContent='center'
                alignItems='center'
              >
                <Typography style={{ fontSize: 18 }}>{questionValue}</Typography>
              </Grid>
            </Grid>
          </div>
          )}
      {question.tooltip &&
        question.tooltipAtBottom &&
        renderSurveyQuestionToolitp(question.tooltip, true)}
    </div>
  )
}

const SurveyDatePicker = ({ id, hideError, nullValue, ...props }) => {
  const [field, meta, helpers] = useField(id)
  const { setValue } = helpers

  return (
    <>
      <Grid container wrap='nowrap'>
        <Grid item xs>
          <CustomDatePickerWithError
            {...field}
            {...meta}
            clearable
            fullWidth
            format='YYYY-MM-DD'
            inputVariant='outlined'
            autoOk
            value={field?.value ? moment.utc(field.value).format(dateFormat) : null}
            onChange={e => {
              const parsedValue = e ? moment.utc(e, dateFormat, true).format(dateFormat) : null
              setValue(parsedValue)
            }}
            {...props}
          />
        </Grid>
        <Grid item>
          <IconButton
            onClick={e => {
              setValue(nullValue || null)
            }}
            size='large'
          >
            <Icon>close</Icon>
          </IconButton>
        </Grid>
      </Grid>
      {!hideError && (
        <ErrorMessage name={id}>
          {msg => (
            <ErrorLabel>
              {msg}
            </ErrorLabel>
          )}
        </ErrorMessage>
      )}
      <div style={{ marginBottom: 20 }} />
    </>
  )
}

function QuestionDate ({
  fieldId,
  question,
  hideError,
  disabled,
  questionValue,
  setFieldValue,
  disableFields,
  version,
  completedSurveys,
  i18n
}) {
  const { tooltip, tooltipAtBottom, isGroup, groupQuestions = [] } = question
  return (
    <div>
      {tooltip && !tooltipAtBottom && (
        <div style={{ marginBottom: 5 }}>{renderSurveyQuestionToolitp(tooltip)}</div>
      )}
      {!disabled && completedSurveys && (
        <PreviouslyAnsweredField
          setFieldValue={setFieldValue}
          completedSurveys={completedSurveys}
          item={question}
          version={version}
        />
      )}
      {isGroup
        ? (
            groupQuestions.map((item, qIndex) => {
              if (item.id && disableFields.includes(item.id)) {
                return null
              }
              let qValue = questionValue
              if (qValue && !qValue[qIndex]) {
                qValue = null
              }
              return (
                <Grid key={qIndex} container direction='row' alignItems='center'>
                  <Grid style={{ flex: 3, margin: 10 }}>
                    <Typography
                      className='survey-question-text'
                      style={{
                        textAlign: 'center',
                        wordBreak: 'break-word'
                      }}
                    >
                      {getLabelFromTranslationData({
                        langVersion: version,
                        data: item.titleValue,
                        i18n
                      })}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    style={{ flex: 2, marginTop: 20 }}
                    direction='column'
                    justifyContent='flex-start'
                  >
                    <SurveyDatePicker
                      disabled={disabled}
                      id={`${fieldId}.${qIndex}`}
                    />
                  </Grid>
                </Grid>
              )
            })
          )
        : (
          <SurveyDatePicker disabled={disabled} id={fieldId} />
          )}
      {tooltip && tooltipAtBottom && renderSurveyQuestionToolitp(tooltip, true)}
    </div>
  )
}

function QuestionDateRange ({
  fieldId,
  question,
  hideError,
  disabled,
  disableFields,
  questionValue,
  setFieldValue,
  version,
  completedSurveys,
  i18n
}) {
  const { isGroup, tooltip, tooltipAtBottom, groupQuestions = [] } = question
  return (
    <div>
      {tooltip && !tooltipAtBottom && (
        <div style={{ marginBottom: 5 }}>{renderSurveyQuestionToolitp(tooltip)}</div>
      )}
      {!disabled && completedSurveys && (
        <PreviouslyAnsweredField
          setFieldValue={setFieldValue}
          completedSurveys={completedSurveys}
          item={question}
          version={version}
        />
      )}
      {isGroup
        ? (
            groupQuestions.map((item, qIndex) => {
              if (item.id && disableFields.includes(item.id)) {
                return null
              }
              const qValue = { ...questionValue }
              if (qValue && !qValue[qIndex]) {
                qValue[qIndex] = {
                  end: null,
                  start: null
                }
              }
              return (
                <Grid
                  key={qIndex}
                  container
                  direction='row'
                  alignItems='center'
                  wrap='nowrap'
                >
                  <Grid container item style={{ flex: 2, margin: 10 }}>
                    <Typography
                      className='survey-question-text'
                      style={{
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        width: '100%'
                      }}
                    >
                      {getLabelFromTranslationData({
                        langVersion: version,
                        data: item.titleValue,
                        i18n
                      })}
                    </Typography>
                  </Grid>

                  <Grid
                    container
                    item
                    style={{ flex: 3, marginTop: 20 }}
                    justifyContent='center'
                    alignItems='center'
                  >
                    <Grid item xs={6} style={{ padding: 5 }}>
                      <SurveyDatePicker
                        label={<Trans>Start</Trans>}
                        id={`${fieldId}.${qIndex}`}
                        disabled={disabled}
                        value={qValue ? qValue[qIndex].start : null}
                        onChange={e => {
                          const toSet = { ...qValue[qIndex] }
                          toSet.start = e ? e.toDate() : null
                          if (!toSet.end) {
                            toSet.end = null
                          }
                          setFieldValue(`${fieldId}.${qIndex}`, toSet)
                        }}
                        nullValue={{
                          ...qValue[qIndex],
                          start: null
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} style={{ padding: 5 }}>
                      <SurveyDatePicker
                        label={<Trans>End</Trans>}
                        id={`${fieldId}.${qIndex}`}
                        disabled={disabled}
                        value={qValue ? qValue[qIndex].end : null}
                        onChange={e => {
                          const toSet = { ...qValue[qIndex] }
                          toSet.end = e ? e.toDate() : null
                          if (!toSet.start) {
                            toSet.start = null
                          }
                          setFieldValue(`${fieldId}.${qIndex}`, toSet)
                        }}
                        nullValue={{
                          ...qValue[qIndex],
                          end: null
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )
            })
          )
        : (
          <Grid container direction='row' style={{ padding: 5 }}>
            <Grid item xs={6} style={{ padding: 5 }}>
              <SurveyDatePicker
                label={<Trans>Start</Trans>}
                id={fieldId}
                disabled={disabled}
                value={
                questionValue && questionValue.start
                  ? questionValue.start
                  : null
              }
                onChange={e => {
                  setFieldValue(`${fieldId}`, {
                    ...questionValue,
                    start: e ? e.toDate() : null
                  })
                }}
                nullValue={{
                  ...questionValue,
                  start: null
                }}
              />
            </Grid>
            <Grid item xs={6} style={{ padding: 5 }}>
              <SurveyDatePicker
                label={<Trans>End</Trans>}
                id={fieldId}
                disabled={disabled}
                value={
                questionValue && questionValue.end ? questionValue.end : null
              }
                onChange={e => {
                  setFieldValue(`${fieldId}`, {
                    ...questionValue,
                    end: e ? e.toDate() : null
                  })
                }}
                nullValue={{
                  ...questionValue,
                  end: null
                }}
              />
            </Grid>
          </Grid>
          )}
      {tooltip && tooltipAtBottom && renderSurveyQuestionToolitp(tooltip, true)}
    </div>
  )
}

function QuestionSelectOne ({
  fieldId,
  nonApplicableDisabled,
  question,
  hideError,
  isSubmitting,
  disabled,
  completedSurveys,
  version
}) {
  return (
    <Grid
      container
      justifyContent='center'
      alignItems='center'
      direction='column'
    >
      {question.tooltip &&
              !question.tooltipAtBottom &&
              renderSurveyQuestionToolitp(question.tooltip)}
      {!disabled && completedSurveys && (
        <PreviouslyAnsweredField
          completedSurveys={completedSurveys}
          item={question}
          version={version}
        />
      )}
      {question.isGroup
        ? <SurveyGroupQuestion
            id={fieldId}
            options={question.options}
            disabled={disabled || isSubmitting}
            groupQuestions={question.groupQuestions}
            canBeNonApplicable={question.nonApplicable}
            hideError={hideError}
            required={question.isRequired}
          />
        : <SurveySelectOneQuestion
            nonApplicableDisabled={nonApplicableDisabled}
            disabled={disabled || isSubmitting}
            id={fieldId}
            hideError={hideError}
            question={question}
          />}
      {question.tooltip &&
              question.tooltipAtBottom &&
              renderSurveyQuestionToolitp(question.tooltip, true)}
    </Grid>
  )
}

function QuestionPointsGenerator ({
  fieldId,
  nonApplicable,
  nonApplicableDisabled,
  question,
  isSubmitting,
  hideError,
  i18n,
  disabled,
  questionValue,
  disableFields,
  completedSurveys,
  version
}) {
  const options = fixedQuestionAnswers.SELECT_YESNO
  return (
    <Field>
      {({ form: { values, setFieldValue, setValues } }) => {
        return (
          <Grid container direction='column' alignItems='flex-start'>
            {question.tooltip &&
              !question.tooltipAtBottom &&
              renderSurveyQuestionToolitp(question.tooltip)}
            {!disabled && completedSurveys && (
              <PreviouslyAnsweredField
                setFieldValue={setFieldValue}
                completedSurveys={completedSurveys}
                item={question}
                version={version}
              />
            )}
            <ScrollBar className='position-relative flex pb-16 w-100'>
              {question.isGroup
                ? (
                    question.groupQuestions && (
                      <TableContainer style={{ overflowX: 'visible' }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell style={{ width: '200px' }} />
                              {options.map((item, index) => {
                                return (
                                  <TableCell
                                    key={index}
                                    align='center'
                                    style={{ width: '150px' }}
                                  >
                                    <p
                                      style={{
                                        textAlign: 'center',
                                        wordBreak: 'break-word',
                                        minWidth: '150px'
                                      }}
                                    >
                                      <Trans id={item} />
                                    </p>
                                  </TableCell>
                                )
                              })}
                              {question.nonApplicable && (
                                <TableCell
                                  align='center'
                                  style={{ width: '150px' }}
                                >
                                  <p
                                    style={{
                                      textAlign: 'center',
                                      wordBreak: 'break-word',
                                      minWidth: '150px'
                                    }}
                                  >
                                    <Trans>Is non applicable</Trans>
                                  </p>
                                </TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {question.groupQuestions &&
                          question.groupQuestions.map((item, qIndex) => {
                            const nonApplicableSelected =
                              nonApplicable && nonApplicable[qIndex]
                            const toRet = (
                              <TableRow key={qIndex}>
                                <TableCell>
                                  <Typography
                                    style={{
                                      wordWrap: true,
                                      width: '180px',
                                      wordBreak: 'break-word',
                                      padding: 10
                                    }}
                                  >
                                    {getLabelFromTranslationData({
                                      langVersion: version,
                                      data: item.titleValue,
                                      i18n
                                    })}
                                  </Typography>
                                  {Boolean(
                                    !hideError && !nonApplicableSelected
                                  ) && (
                                    <ErrorMessage name={`${fieldId}.${qIndex}`}>
                                      {msg => (
                                        <ErrorLabel>
                                          {msg}
                                        </ErrorLabel>
                                      )}
                                    </ErrorMessage>
                                  )}
                                </TableCell>
                                {options.map((item, index) => {
                                  let isChecked = false
                                  if (questionValue) {
                                    isChecked =
                                      questionValue[qIndex] ===
                                      indexToOption(index)
                                  }
                                  return (
                                    <TableCell key={index} align='center'>
                                      <Radio
                                        style={{ marginLeft: 10 }}
                                        key={index}
                                        checked={isChecked}
                                        value={indexToOption(index)}
                                        onChange={e => {
                                          setFieldValue(
                                            `${fieldId}.${qIndex}`,
                                            e.target.value
                                          )
                                        }}
                                        disabled={
                                          isSubmitting ||
                                          disabled ||
                                          nonApplicableSelected
                                        }
                                      />
                                      {isChecked && item.requireDetails && (
                                        <div
                                          style={{
                                            marginLeft: 15,
                                            marginRight: 15
                                          }}
                                        >
                                          <TextFieldWithFormik
                                            name={
                                              'other.' + `${fieldId}.${qIndex}`
                                            }
                                            variant='outlined'
                                            placeholder={myI18n._(
                                              t`Please, specify`
                                            )}
                                            fullWidth
                                            disabled={disabled}
                                            InputProps={
                                              item.requireDetails === 'number'
                                                ? {
                                                    inputComponent:
                                                      NumberFormatCustom
                                                  }
                                                : {}
                                            }
                                          />
                                        </div>
                                      )}
                                    </TableCell>
                                  )
                                })}
                                {question.nonApplicable && (
                                  <TableCell align='center'>
                                    <Checkbox
                                      style={{
                                        marginLeft: 10,
                                        marginTop: 3
                                      }}
                                      checked={nonApplicableSelected}
                                      onChange={e => {
                                        const toSet = { ...values }
                                        toSet[fieldId] = [...toSet[fieldId]]
                                        toSet[fieldId][qIndex] = ''
                                        if (
                                          !toSet[fieldId + nonApplicableSuffix]
                                        ) {
                                          toSet[fieldId + nonApplicableSuffix] =
                                            []
                                        }
                                        toSet[fieldId + nonApplicableSuffix][
                                          qIndex
                                        ] = e.target.checked
                                        setValues(toSet)
                                      }}
                                      disabled={nonApplicableDisabled}
                                    />
                                  </TableCell>
                                )}
                              </TableRow>
                            )
                            if (item.id && disableFields.includes(item.id)) {
                              return null
                            } else {
                              return toRet
                            }
                          })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  )
                : (
                  <Field
                    component={RadioGroup}
                    name={fieldId}
                    disabled={disabled}
                    style={{ width: '100%', marginTop: 5 }}
                  >
                    {options.map((item, index) => {
                      const selected = questionValue === indexToOption(index)
                      return (
                        <Grid key={index} container direction='column'>
                          <FormControlLabel
                            style={{ marginLeft: 10, marginTop: 3 }}
                            key={index}
                            value={indexToOption(index)}
                            control={<Radio disabled={isSubmitting} />}
                            label={
                              <Typography style={{ textAlign: 'left' }}>
                                <Trans id={item} />
                              </Typography>
                          }
                            onChange={e => {
                              if (nonApplicable) {
                                setFieldValue(
                                `${question.id}` + nonApplicableSuffix,
                                false
                                )
                              }
                            }}
                            disabled={isSubmitting || disabled}
                          />
                          {selected && item.requireDetails && (
                            <div style={{ marginLeft: 15, marginRight: 15 }}>
                              <TextFieldWithFormik
                                name={`${fieldId}` + '_other'}
                                variant='outlined'
                                placeholder={myI18n._(t`Please, specify`)}
                                fullWidth
                                disabled={disabled}
                                InputProps={
                                item.requireDetails === 'number'
                                  ? { inputComponent: NumberFormatCustom }
                                  : {}
                              }
                              />
                            </div>
                          )}
                        </Grid>
                      )
                    })}
                    {question.nonApplicable && (
                      <FormControlLabel
                        style={{ marginLeft: 10, marginTop: 3 }}
                        checked={nonApplicable}
                        control={<Checkbox />}
                        label={
                          <Typography style={{ textAlign: 'left' }}>
                            <Trans>Is non applicable</Trans>
                          </Typography>
                      }
                        onChange={e => {
                          const toSet = { ...values }
                          toSet[question.id + nonApplicableSuffix] =
                          e.target.checked
                          toSet[question.id] = questionToInitial(question)[1]
                          setValues(toSet)
                        }}
                        disabled={nonApplicableDisabled}
                      />
                    )}
                  </Field>
                  )}
            </ScrollBar>
            {question.tooltip &&
              question.tooltipAtBottom &&
              renderSurveyQuestionToolitp(question.tooltip, true)}
          </Grid>
        )
      }}
    </Field>
  )
}

const QuestionMultipleValue = ({
  fieldId,
  question,
  disabled,
  completedSurveys,
  setFieldValue,
  version,
  questionValue = []
}) => {
  const clearOptionSelected = question.options.some((item, index) => {
    if (questionValue[index] && item.clearOnSelect) {
      return true
    } else {
      return false
    }
  })

  return (
    <Field>
      {({ form: { values } }) => (
        <>
          <div style={{ padding: 8 }}>
            {!question.tooltipAtBottom && question.tooltip
              ? (
                  renderSurveyQuestionToolitp(question.tooltip)
                )
              : (
                <Trans> Select all that apply </Trans>
                )}
          </div>
          {!disabled && completedSurveys && (
            <PreviouslyAnsweredField
              setFieldValue={setFieldValue}
              completedSurveys={completedSurveys}
              item={question}
              version={version}
            />
          )}
          <FormGroup style={{ width: '100%', padding: 8 }}>
            {question.options.map((opt, index) => {
              let selected = false
              selected = questionValue[index]
              return (
                <Grid
                  key={index}
                  container
                  direction='column'
                  style={{ width: '100%', paddingBottom: 20 }}
                >
                  <FormControlLabel
                    name={`${fieldId}[${index}]`}
                    disabled={
                      disabled || Boolean(clearOptionSelected && !selected)
                    }
                    checked={Boolean(selected)}
                    onChange={e => {
                      if (opt.clearOnSelect) {
                        const toSet = Array(question.options.length).fill(false)
                        toSet[index] = e.target.checked
                        setFieldValue(`${fieldId}`, toSet)
                      } else {
                        setFieldValue(`${fieldId}[${index}]`, e.target.checked)
                        if (
                          values[fieldId + '_other'] &&
                          values[fieldId + '_other'][index]
                        ) {
                          setFieldValue(`${fieldId}_other.${index}`, null)
                        }
                      }
                    }}
                    control={<Checkbox indeterminate={false} />}
                    label={
                      <Typography style={{ textAlign: 'left' }}>
                        {opt.text}
                      </Typography>
                    }
                  />
                  {selected && opt.requireDetails && (
                    <TextFieldWithFormik
                      style={{ marginLeft: 15, marginRight: 15 }}
                      name={`${fieldId}_other.${index}`}
                      variant='outlined'
                      placeholder={myI18n._(t`Please, specify`)}
                      fullWidth
                      disabled={disabled}
                      InputProps={
                        opt.requireDetails === 'number'
                          ? { inputComponent: NumberFormatCustom }
                          : {}
                      }
                    />
                  )}
                </Grid>
              )
            })}
          </FormGroup>
          {question.tooltipAtBottom && question.tooltip
            ? renderSurveyQuestionToolitp(question.tooltip, true)
            : null}

        </>
      )}
    </Field>
  )
}

function NumberFormatCustom (props) {
  const { inputRef, onChange, ...other } = props
  return (
    <NumberFormat
      {...other}
      value={typeof props.value === 'object' ? '' : props.value}
      defaultValue=''
      thousandSeparator=' '
      isNumericString
      getInputRef={inputRef}
      allowNegative={false}
      onValueChange={values => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        })
      }}
    />
  )
}

export const renderSurveyQuestionToolitp = (tooltipText, atBottom = false) => {
  const paragraphs = tooltipText.split('\n')

  return (
    <Box sx={{
      width: '100%',
      pt: atBottom ? '13px' : 0,
      pb: atBottom ? 0 : '13px'
    }}
    >
      {paragraphs.map((item, index) => (
        <Typography
          key={index}
          component='span'
          className='survey-question-tooltip'
          sx={{
            display: 'block',
            m: 0
          }}
        >
          {item}
          {index < paragraphs.length - 1 &&
            <>
              <br />
              <br />
            </>}
        </Typography>
      ))}
    </Box>
  )
}

export {
  QuestionDate,
  QuestionDateRange,
  QuestionInputText,
  QuestionMultipleValue,
  QuestionNumericSlider,
  QuestionPointsGenerator,
  QuestionSelectOne,
  QuestionUploadFiles
}

